/**
 * Contexto y helpers de la sección de cierre.
 *
 * Separado del componente por la misma razón que `period.ts` y `tones.ts`: un
 * archivo que exporta componentes y constantes a la vez rompe el refresco en
 * caliente de Vite.
 */

import { useOutletContext } from "react-router-dom";
import type {
  ClosingPackage,
  Config,
  SessionInfo,
} from "../../api/types";
import { may } from "../../api/permissions";

export interface ClosingContext {
  package: ClosingPackage;
  config: Config;
  session: SessionInfo;
  period: { periodFrom: string; periodTo: string };
}

/**
 * Si se ofrece el panel de apuntes.
 *
 * Ocultar un botón es comodidad, no autorización: el servidor exige
 * `journal:read` en el endpoint y devuelve 403 sin él. Esto solo evita ofrecer
 * una acción que ya sabemos que va a fallar. En desarrollo local no hay sesión
 * con permisos y se ofrece todo, igual que hace `require()` en el backend.
 */
export function mayReadJournal(session: SessionInfo): boolean {
  return may(session, "journal:read");
}

/**
 * El paquete se pide UNA vez, en la ruta de layout, y todas las pantallas leen
 * el mismo objeto. Es la misma razón por la que el backend lo construye
 * entero: si cada pantalla se alimentara por su cuenta, el tablero acabaría
 * dividiendo entre una cifra que el estado de al lado no reconoce, que es
 * exactamente lo que pasa hoy en la hoja de cálculo.
 */
export function useClosing(): ClosingContext {
  return useOutletContext<ClosingContext>();
}

/*
 * Aquí vivían `HOMOLOGATION_LABEL` y `HOMOLOGATION_TONE`. El estado de
 * homologación ya no se dibuja —ver `shared.tsx`—, y un mapa de rótulos sin
 * lector es una invitación a volver a pintarlo. El TIPO se queda: el campo
 * sigue viajando en la API y lo consumen las pantallas de administración.
 */

/** Diferencia contable: cero se declara, no se omite. */
export function isBalanced(difference: number | null): boolean {
  return difference !== null && Math.abs(difference) <= 0.01;
}
